/**
 * TabBar — Chat / Spine tab row, Variant C chat-first redesign.
 *
 * Spec 29 Phase 1. Replaces the retired ``SpineTimelineToggle``.
 * Sits between TopBar + ContextStrip above and the tab body below.
 *
 * SRP: pure presentation. Receives activeTab + showSpine + callback;
 * emits tab-change intent. No state, no side effects.
 *
 * # DECISION: the Spine tab is hidden entirely when ``showSpine`` is
 * #   false, not disabled with a tooltip.
 * # WHY: writer feedback (2026-04-23) — "chat interface central;
 * #      spine in a second tab whenever a task involves a drafting
 * #      sequence." A disabled tab is still visual chrome; a hidden
 * #      tab matches the "VERY CLEAN and NON-CLUNKY" bar. When no
 * #      run is in flight the writer sees only the chat tab (no
 * #      tab row at all — see renderTabRow below).
 * # DATE: 2026-04-23
 */

import type React from "react";
import { useCallback, useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TabId = "chat" | "spine" | "issues" | "drafts" | "images";

export interface TabBarProps {
  /** Currently-active tab. */
  activeTab: TabId;
  /** Intent callback fired when the writer clicks a tab or uses the
   *  keyboard (arrow keys / Enter / Space). */
  onTabChange: (tab: TabId) => void;
  /** When false, the Spine tab is hidden entirely AND the whole tab
   *  row collapses to zero height (the caller can skip rendering the
   *  component, but hiding it here keeps the parent layout simpler).
   *  When true, both tabs render. */
  showSpine: boolean;
  /** When true, render the Issues tab beside Spine — the submission-wide list
   *  of every gap / regulatory issue across the run. Shown whenever the Spine
   *  is (a run exists / sections present). */
  showIssues?: boolean;
  /** Optional — compact counter chip on the Issues tab showing the number of
   *  open issues across the submission. Null hides the chip. */
  issuesCounter?: string | null;
  /** When true, render the Drafts tab beside Chat / Spine. The
   *  Drafts tab is run-agnostic — it lists every prior completed
   *  run for the current scope (doc / workspace / library) — so the
   *  caller may show it independently of ``showSpine``. */
  showDrafts?: boolean;
  /** When true, render the Images tab — the image-search view (browse +
   *  insert the library's figures). Run-agnostic, like Drafts; shown
   *  whenever a library is bound. */
  showImages?: boolean;
  /** Optional — compact counter chip on the Spine tab for at-a-glance
   *  progress ("5/38") while a run is in flight. Null hides the chip. */
  spineCounter?: string | null;
  /** When true, the Spine tab renders a pulsing ``hot``-status dot
   *  next to its label so the writer sees that drafting is active
   *  without leaving the Chat tab. Caller drives this from the run
   *  lifecycle (typically ``state.state === "mid-run"``). The dot
   *  is purely visual progress signal; the ``spineCounter`` chip
   *  remains the source of truth for "how far". */
  spineActive?: boolean;
  /** Handler for the Stop/Reset button at the far right of the tab
   *  row. When provided the button renders; when omitted it is hidden.
   *  Caller passes ``handleStopRun`` (mid-run/approval) or
   *  ``handleReset`` (post-run) — same handlers previously wired to
   *  CommandRail's ``onReset`` prop. */
  onReset?: () => void;
  /** Label for the Stop/Reset button. Caller passes "stop" during
   *  mid-run/approval and "reset" post-run — identical to the
   *  ``resetLabel`` prop previously passed to CommandRail. */
  resetLabel?: string;
  /** Optional Save action — flushes pending debounced session patches
   *  and any write-back-bridge edit buffer so the writer has a single
   *  trustworthy "all my changes are committed" button. Only renders
   *  when provided. */
  onSave?: () => void | Promise<void>;
  /** When true, the Save button shows a "Saving…" busy label and is
   *  disabled to prevent double-clicks. */
  saveBusy?: boolean;
  /** Display label for the Save button. Caller swaps to "Saved" briefly
   *  after a successful flush. Defaults to "Save". */
  saveLabel?: string;
  /** When true, the Save button is disabled with ``saveDisabledTooltip``
   *  shown on hover. Used while a live draft run is in flight so the
   *  writer can't snapshot a half-built draft. */
  saveDisabled?: boolean;
  /** Hover tooltip shown when ``saveDisabled`` is true. */
  saveDisabledTooltip?: string;
  /** Non-null when the last Save failed (fetch failure or per-section
   *  errors) — renders the button in the error color with the message as
   *  its hover tooltip, so a failed Save can never read as "Saved". */
  saveError?: string | null;
}

export const TabBar: React.FC<TabBarProps> = ({
  activeTab,
  onTabChange,
  showSpine,
  showIssues = false,
  issuesCounter = null,
  showDrafts = false,
  showImages = false,
  spineCounter = null,
  spineActive = false,
  onReset,
  resetLabel,
  onSave,
  saveBusy = false,
  saveLabel,
  saveDisabled = false,
  saveDisabledTooltip,
  saveError = null,
}) => {
  // ARIA: the active tab's button gets aria-selected; keyboard
  // navigation (Left/Right arrows) cycles through visible tabs. Keep
  // a ref to the container so we can focus a neighbouring tab button
  // on key press without reaching outside the component.
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      // Build the ordered list of visible tabs so arrow keys cycle
      // through whatever is actually rendered.
      const order: TabId[] = ["chat"];
      if (showSpine) order.push("spine");
      if (showIssues) order.push("issues");
      if (showDrafts) order.push("drafts");
      if (showImages) order.push("images");
      if (order.length < 2) return; // only one visible → nothing to navigate
      e.preventDefault();
      const idx = order.indexOf(activeTab);
      const delta = e.key === "ArrowRight" ? 1 : -1;
      const next = order[(idx + delta + order.length) % order.length];
      onTabChange(next);
    },
    [activeTab, onTabChange, showSpine, showIssues, showDrafts, showImages],
  );

  // Auto-focus the newly-active tab button so screen readers track
  // the selection change. Safe — this effect only fires on tab
  // swaps, not on every render.
  useEffect(() => {
    if (!containerRef.current) return;
    const selector = `[data-tab-id="${activeTab}"]`;
    const btn = containerRef.current.querySelector<HTMLButtonElement>(selector);
    // Only steal focus if focus was already inside the tab row (the
    // writer initiated the change via keyboard). Otherwise don't
    // hijack focus away from the chat input.
    const activeElement = document.activeElement;
    const focusInside = activeElement instanceof HTMLElement && containerRef.current.contains(activeElement);
    if (focusInside && btn) btn.focus();
  }, [activeTab]);

  // The design keeps Save out of the tab row: edits auto-save. The control
  // appears only while a save is in flight, just finished, or needs the
  // writer (failed / not saved); "Save now" also lives in the account menu.
  const saveNeedsAttention = Boolean(saveError) || saveBusy || (saveLabel != null && saveLabel !== "Save");
  const tab = (id: TabId, label: string, extra?: React.ReactNode, lead?: React.ReactNode) => (
    <button
      type="button"
      role="tab"
      data-tab-id={id}
      aria-selected={activeTab === id}
      aria-controls={`tab-panel-${id}`}
      tabIndex={activeTab === id ? 0 : -1}
      className="as-tab"
      onClick={() => onTabChange(id)}
    >
      {lead}
      {label}
      {extra}
    </button>
  );

  return (
    <div ref={containerRef} className="as-tabs as-tabbar" role="tablist" aria-label="View" onKeyDown={handleKeyDown}>
      {tab("chat", "Chat")}
      {showSpine &&
        tab(
          "spine",
          "Spine",
          spineCounter ? (
            <span className="as-count" aria-label={`Progress: ${spineCounter}`}>
              {spineCounter}
            </span>
          ) : null,
          spineActive ? <span className="as-dot-live" role="status" aria-label="Drafting in progress" /> : null,
        )}
      {showIssues &&
        tab(
          "issues",
          "Issues",
          issuesCounter ? (
            <span
              className={issuesCounter === "0" ? "as-count" : "as-count as-count-alert"}
              aria-label={`${issuesCounter} open issues`}
            >
              {issuesCounter}
            </span>
          ) : null,
        )}
      {showDrafts && tab("drafts", "Drafts")}
      {showImages && tab("images", "Images")}

      {onSave && saveNeedsAttention && (
        <button
          type="button"
          className={saveError ? "as-tabbar-save as-tabbar-save-error" : "as-tabbar-save"}
          onClick={() => {
            void onSave();
          }}
          disabled={saveBusy || saveDisabled}
          title={
            saveDisabled
              ? (saveDisabledTooltip ?? "Save is unavailable right now")
              : (saveError ?? "Flush any pending edits to the backend")
          }
        >
          {saveBusy ? "Saving…" : (saveLabel ?? "Save")}
        </button>
      )}
      {onReset && resetLabel !== "reset" && (
        <button type="button" className="as-tabbar-save as-tabbar-save-error" onClick={onReset}>
          {resetLabel}
        </button>
      )}
    </div>
  );
};

export default TabBar;
